import * as React from 'react';
import { Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  DashboardTableSearchInput,
  buildDashboardDataTableColumns,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useSupplyDomainsQuery } from '../../hooks';
import {
  formatSupplyDate,
  resolveSupplyStatusLabel,
  resolveSupplyStatusTone,
  supplyPageSizes,
  type SupplyDomain,
} from '../../model';
import { useSupplyDomainsRouteFilters } from '../../useSupplyRouteFilters';
import { SupplyDomainDialog } from '../SupplyDomainDialog';
import { SupplyTableRowActions } from '../SupplyTableRowActions';

export function SupplyDomainsTable() {
  const { can } = useShigoPermissions();
  const canWrite = can(ShigoPermission.MadBuySupplyWrite);
  const { filter, setSearch, setPagination } = useSupplyDomainsRouteFilters();
  const [dialogDomain, setDialogDomain] = React.useState<SupplyDomain | null | undefined>();
  const query = useSupplyDomainsQuery(filter);
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<SupplyDomain>([
        {
          id: 'url',
          kind: DashboardDataTableColumnKind.Text,
          header: dashboardT('supply.url'),
          headerClassName: 'pl-4',
          className: 'pl-4 font-medium',
          value: (item) => item.url,
        },
        {
          id: 'status',
          kind: DashboardDataTableColumnKind.Status,
          header: dashboardT('supply.status'),
          label: (item) => resolveSupplyStatusLabel(item.status),
          tone: (item) => resolveSupplyStatusTone(item.status),
        },
        {
          id: 'created',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('supply.created'),
          headerClassName: 'w-28',
          className: 'w-28 whitespace-nowrap',
          value: (item) => formatSupplyDate(item.createdOn),
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          hidden: !canWrite,
          cell: (item) => (
            <SupplyTableRowActions
              onEdit={() => {
                setDialogDomain(item);
              }}
            />
          ),
        },
      ]),
    [canWrite],
  );
  return (
    <>
      <DashboardDataTable
        title={dashboardT('supply.domains.title')}
        tableLabel={dashboardT('supply.domains.title')}
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={(item) => item.id}
        loading={query.isLoading}
        errorMessage={query.isError ? dashboardT('supply.loadError') : undefined}
        emptyMessage={dashboardT('supply.domains.empty')}
        minWidthClassName="min-w-[640px]"
        toolbar={
          <>
            <DashboardTableSearchInput
              aria-label={dashboardT('supply.search')}
              value={filter.searchTerm}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
            {canWrite ? (
              <Button
                onClick={() => {
                  setDialogDomain(null);
                }}
              >
                <Plus className="h-4 w-4" />
                {dashboardT('supply.domains.create')}
              </Button>
            ) : null}
          </>
        }
        pagination={{
          value: {
            page: filter.page,
            pageSize: filter.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: supplyPageSizes,
          disabled: query.isLoading,
          onChange: (next) => {
            setPagination(next.page, next.pageSize);
          },
        }}
      />
      {canWrite && dialogDomain !== undefined ? (
        <SupplyDomainDialog
          domain={dialogDomain}
          onClose={() => {
            setDialogDomain(undefined);
          }}
        />
      ) : null}
    </>
  );
}
